import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ROLE_PERMISSIONS } from "@genius-tracker/api-client/permission-catalog";
import { useLocale } from "@genius-tracker/i18n/react";
import { fetchSessionWithPostLoginGrace, redirectToLogin, type SessionInfo } from "../lib/api.js";

/**
 * Admin-console session provider (Sprint 7, BFF pattern).
 *
 * Identical behaviour to the web-app provider: fetches
 * `/v1/auth/session` on mount, redirects to `/v1/auth/login` when
 * unauthenticated. The admin UI never renders its real content
 * until a verified session is in hand.
 */

type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; session: SessionInfo }
  | { status: "redirecting" };

const SessionContext = createContext<SessionInfo | null>(null);

export function useSession(): SessionInfo {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession() used outside <SessionProvider>.");
  return ctx;
}

/**
 * Test-only export of the SessionContext so admin-app component
 * tests can inject a fake `SessionInfo` without booting the BFF
 * round trip. Mirrors the equivalent in `apps/web`.
 */
function TestSessionProvider({
  children,
  value,
}: {
  readonly children: ReactNode;
  readonly value: SessionInfo;
}) {
  const queryClient = useQueryClient();

  if (
    !(globalThis as { __GT_ADMIN_DISABLE_ACCESS_FIXTURE__?: boolean })
      .__GT_ADMIN_DISABLE_ACCESS_FIXTURE__
  ) {
    queryClient.setQueryData(["me", "access", value.tenantId, value.identity.sub], {
      // The test provider mirrors the server's access projection from the
      // supplied session roles. This remains test-only: production always
      // obtains the projection through GET /v1/me/access.
      permissions: [...new Set(value.roles.flatMap((role) => ROLE_PERMISSIONS[role] ?? []))],
    });
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Test session injection also seeds the server-authoritative access query.
 * Platform owners receive the complete platform projection; every other
 * role receives an explicit empty projection. This keeps role-only tests
 * honest after access moved to `/v1/me/access`, without changing production
 * authorization or adding a production fallback.
 */
export const __SessionContextForTesting = { Provider: TestSessionProvider };

export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    const ctl = new AbortController();
    fetchSessionWithPostLoginGrace(ctl.signal)
      .then((session) => {
        if (ctl.signal.aborted) return;
        if (!session) {
          setState({ status: "redirecting" });
          redirectToLogin();
          return;
        }
        setState({ status: "authenticated", session });
      })
      .catch((err: unknown) => {
        if (ctl.signal.aborted) return;
        console.error("admin.session.fetch.failed", err);
        setState({ status: "redirecting" });
        redirectToLogin();
      });
    return () => ctl.abort();
  }, []);

  const session = state.status === "authenticated" ? state.session : null;
  const value = useMemo(() => session, [session]);

  if (!value) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minBlockSize: "100dvh",
          display: "grid",
          placeItems: "center",
          color: "var(--gt-sys-text-muted)",
          font: "500 14px/1.4 system-ui, sans-serif",
        }}
      >
        <span>
          {state.status === "redirecting" ? t("auth.redirecting") : t("auth.session.loading")}
        </span>
      </div>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
